/* global window */

const BROADCAST_CHANNEL = 'aos-history'
const LEGACY_MIGRATED_KEY = 'historyMigrated'

const HISTORY_INDEXES = {
	order: 'orders',
	customer: 'customers',
}

const HISTORY_FIELDS = {
	order: [
		'_id',
		'ref',
		'externalRef',
		'status',
		'source',
		'instance',
		'version',
		'created',
		'edited',
		'customer',
		'creator',
		'owner',
		'totalAmount',
		'totalPaid',
		'billing',
	],
	customer: ['_id', 'name', 'email', 'customerType', 'instance', 'billing'],
}

const HISTORY_SHELLS = {
	order: ['billing'],
	customer: ['billing'],
}

let channel
let refreshPromise

const historyUtils = {
	HISTORY_KEY: 'history',
	MAX_HISTORY: 100,
	MAX_DAYS: 7,

	historyLimits() {
		return {
			maxEntries: this.$config?.history?.maxEntries || this.MAX_HISTORY,
			maxDays: this.$config?.history?.maxDays || this.MAX_DAYS,
		}
	},

	formatDateTime(date) {
		const pad = (num) => String(num).padStart(2, '0')

		return (
			`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
			`${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
		)
	},

	historyKey(entry) {
		return [entry.type, entry.instance, entry._id].map((part) => String(part ?? '')).join('/')
	},

	historyChannel() {
		if (channel !== undefined) return channel

		channel = window.BC?.BroadcastChannel ? new window.BC.BroadcastChannel(BROADCAST_CHANNEL) : null

		if (channel)
			channel.addEventListener('message', (message) => {
				localStorage.setItem(this.HISTORY_KEY, JSON.stringify(message?.history ?? []))
				this.emitHistoryChanged()
			})

		return channel
	},

	readHistory() {
		try {
			return JSON.parse(localStorage.getItem(this.HISTORY_KEY) || '[]')
		} catch (e) {
			console.warn('Discarding unreadable history cache', e)
			return []
		}
	},

	emitHistoryChanged() {
		window.dispatchEvent(
			new CustomEvent('historyChanged', {
				detail: {
					storage: localStorage.getItem(this.HISTORY_KEY),
				},
			}),
		)
	},

	writeHistory(history) {
		try {
			localStorage.setItem(this.HISTORY_KEY, JSON.stringify(history))
		} catch (e) {
			console.warn('Could not cache history locally', e)
			localStorage.removeItem(this.HISTORY_KEY)
		}

		this.emitHistoryChanged()
		this.historyChannel()?.postMessage({ history })

		return history
	},

	normalizeEntry(entry) {
		;(HISTORY_SHELLS[entry.type] || []).forEach((path) => {
			if (!_.get(entry, path)) _.set(entry, path, {})
		})

		return entry
	},

	historyObjectId(entry) {
		return entry.type == 'customer' ? `${entry.instance}-${entry._id}` : String(entry._id)
	},

	hydrateEntries(entries) {
		if (!entries.length) return Promise.resolve([])

		return Promise.all(
			Object.entries(HISTORY_INDEXES).map(([type, index]) => {
				const subset = entries.filter((entry) => entry.type == type)
				if (!subset.length) return []

				if (!this.$searchAlgolia?.[index]) return []

				return this.$searchAlgolia[index]
					.getObjects(
						subset.map((entry) => this.historyObjectId(entry)),
						{ attributesToRetrieve: HISTORY_FIELDS[type] },
					)
					.then(({ results }) => subset.map((entry, offset) => [this.historyKey(entry), results[offset]]))
					.catch((e) => {
						console.warn(`Could not refresh ${type} history from the search index`, e)
						return []
					})
			}),
		)
			.then((lookups) => new Map(lookups.flat().filter(([, entity]) => entity)))
			.then((hydrated) =>
				entries.map((entry) => {
					const live = hydrated.get(this.historyKey(entry))

					return this.normalizeEntry({
						...entry,
						..._.pick(live, HISTORY_FIELDS[entry.type]),
						_id: entry._id,
						type: entry.type,
						instance: entry.instance,
					})
				}),
			)
	},

	addToHistory(type, historyEntry) {
		if (!HISTORY_FIELDS[type]) throw new Error(`Unknown history type "${type}"`)

		const entry = this.normalizeEntry({
			..._.pick(historyEntry, HISTORY_FIELDS[type]),
			..._.pick(historyEntry, ['timestamp', 'dateString', 'dateFormatted']),
			type,
		})

		const optimistic = this.readHistory().filter((item) => this.historyKey(item) !== this.historyKey(entry))

		optimistic.unshift(entry)
		this.cleanupHistory(optimistic)

		return this.$http
			.post('/api/histories', {
				type,
				instance: entry.instance,
				entityId: entry._id,
				fields: entry,
			})
			.then(({ data }) => {
				const stored = this.normalizeEntry({ ...entry, ...data })
				const reconciled = this.readHistory().filter(
					(item) => this.historyKey(item) !== this.historyKey(stored),
				)

				reconciled.unshift(stored)
				return this.cleanupHistory(reconciled)
			})
			.catch((e) => {
				console.warn('Could not persist history entry', e)
				return this.readHistory()
			})
	},

	refreshHistory() {
		if (refreshPromise) return refreshPromise

		return (refreshPromise = Promise.resolve()
			.then(() => this.migrateLegacyHistory())
			.then(() => this.$http.get('/api/histories'))
			.then(({ data }) => this.hydrateEntries(data))
			.then((entries) => this.writeHistory(entries))
			.catch((e) => {
				console.warn('Could not load history from server, falling back to local cache', e)
				return this.cleanupHistory()
			})
			.finally(() => (refreshPromise = undefined)))
	},

	migrateLegacyHistory() {
		if (localStorage.getItem(LEGACY_MIGRATED_KEY)) return Promise.resolve()
		localStorage.setItem(LEGACY_MIGRATED_KEY, '1')

		const legacy = this.readHistory().filter((item) => item?.type && item?._id && item?.instance && item?.timestamp)
		if (!legacy.length) return Promise.resolve()

		return Promise.all(
			legacy.map((item) =>
				this.$http.post('/api/histories', {
					type: item.type,
					instance: item.instance,
					entityId: item._id,
					fields: item,
					viewed: new Date(item.timestamp * 1000).toISOString(),
				}),
			),
		).catch((e) => console.warn('Could not migrate local history to the server', e))
	},

	cleanupHistory(updatedHistory) {
		const history = updatedHistory ?? this.readHistory()
		const { maxEntries, maxDays } = this.historyLimits()

		const now = new Date()
		now.setHours(0, 0, 0, 0)

		const oldestAllowedDate = new Date(now)
		oldestAllowedDate.setDate(now.getDate() - maxDays)
		const oldestAllowedTimestamp = oldestAllowedDate.getTime() / 1000

		const filteredHistory = history.filter((item) => {
			if (!item.timestamp) return false
			if (!item.type) return false
			if (!item._id) return false
			return item.timestamp >= oldestAllowedTimestamp
		})

		return this.writeHistory(filteredHistory.slice(0, maxEntries))
	},

	clearHistory(type = 'all') {
		return this.$http.delete('/api/histories', { params: { type } }).then(() => this.refreshHistory())
	},
}

module.exports = historyUtils
